import { Box, Chip, Divider, Fade, Typography, useTheme } from "@mui/material";
import React from "react";
import LucideIcon from "../../../../components/LucideIcon";
import { LeadListResult } from "../types";
import StepNavButtons from "./StepNavButtons";

interface LeadListPreviewStepProps {
  list?: LeadListResult;
  onNext: () => void;
  onBack: () => void;
}

// Build the human-readable "targeted by ..." chips from the agent's targeting choices.
function targetingChips(list?: LeadListResult): string[] {
  const t = list?.targeting;
  if (!t) return [];
  const chips: string[] = [];
  (t.segments || []).forEach((s) => s && chips.push(s));
  (t.businessKeywords || []).forEach((k) => k && chips.push(k));
  (t.seniorities || []).forEach((s) => s && chips.push(s.toUpperCase()));
  if (t.city) chips.push(t.city);
  return chips;
}

// Preview of the starter lead list built from the ICP. The leads come from the AudienceLab
// preview endpoint (a free sample), dropped into a real list the user can open and work from
// the Lists page after onboarding.
export default function LeadListPreviewStep({
  list,
  onNext,
  onBack,
}: LeadListPreviewStepProps) {
  const theme = useTheme();
  const leads = list?.sampleLeads || [];
  const count = list?.leadCount || 0;
  const chips = targetingChips(list);

  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
          Lead List
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
          Your first leads
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          We found in-market buyers matching your ideal customer and added them to a list your
          AI team can start working right away.
        </Typography>

        {!count ? (
          <Typography color="text.secondary">
            We couldn't find matching leads this time. You can build a list anytime from the
            Lists page.
          </Typography>
        ) : (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "action.hover",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                mb: chips.length ? 1.5 : 2,
              }}
            >
              <LucideIcon name="Users" size={20} color={theme.palette.primary.main} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {list?.name || "Starter Leads"}
              </Typography>
              <Chip label={`${count} leads`} size="small" color="primary" />
            </Box>

            {chips.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
                {chips.map((c, i) => (
                  <Chip key={i} label={c} size="small" variant="outlined" />
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {leads.map((lead, i) => (
                <Box key={i}>
                  {i > 0 && <Divider sx={{ my: 1.25 }} />}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <LucideIcon
                      name="User"
                      size={18}
                      color={theme.palette.text.secondary}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {lead.name || "Lead"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {[lead.title, lead.company].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <StepNavButtons onBack={onBack} onNext={onNext} />
      </Box>
    </Fade>
  );
}
